import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  SimpleGrid,
  Textarea,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  Button,
  DefaultModalDialog,
  Image,
  LabeledText,
} from '~/common/components/defaultUIComponents';
import JobTextRecordApi, {
  JobTextRecord,
} from '~/common/api/zeiterfassungServerApi/JobTextRecordApi';
import DepartmentApi from '~/common/api/zeiterfassungServerApi/DepartmentApi';
import JobPictureApi from '~/common/api/zeiterfassungServerApi/JobPictureApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { formatName } from '~/common/utils/employeeUtils';
import { DeleteIcon, ExternalLinkIcon, ViewIcon } from '@chakra-ui/icons';
import { ImageListType } from 'react-images-uploading';
import useTranslate from '~/common/utils/translate';
import { Paper } from '~/common/components/Paper';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import _ from 'lodash';

interface TextRecordListItemProps {
  textRecord: JobTextRecord;
}

const usePictures = (job: JobTextRecord) => {
  const [pictures, setPictures] = React.useState<ImageListType>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  JobPictureApi.hooks.usePicturesOfJobQuery(job.job, {
    enabled: !!job && isOpen,
    onSuccess: (pictures) => {
      setPictures(
        (pictures ?? []).map((pic) => ({
          id: pic.id,
          dataURL: `/images/job/${pic.id}/`,
        })),
      );
    },
  });
  return { pictures, isOpen, onOpen, onClose };
};

export const TextRecordListItem: React.FC<TextRecordListItemProps> = ({
  textRecord,
}) => {
  const { isOpen, onClose, onOpen, pictures } = usePictures(textRecord);
  const translate = useTranslate();
  const navigate = useNavigate();

  const [text, setText] = useState(
    [
      textRecord.job?.description,
      textRecord.comment,
      textRecord.improvement_comment,
    ]
      .filter((e) => !!e)
      .join('\n\n'),
  );
  const [{ data: projects }] = ProjectApi.hooks.useProjectsQuery();
  const project = React.useMemo(
    () => _.find(projects, { id: textRecord.job?.project }),
    [textRecord, projects],
  );
  const [{ data: departments }] = DepartmentApi.hooks.useDepartmentsQuery();
  const department = React.useMemo(
    () => _.find(departments, { id: textRecord.job?.department }),
    [departments, textRecord],
  );

  const { mutate: markAsSeen } = JobTextRecordApi.hooks.useTextRecordSeen();
  const { mutateAsync: setJobDescription } =
    JobTextRecordApi.hooks.useSetJobDescription();

  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const employee = React.useMemo(
    () => employees?.find((e) => parseInt(e.id) === textRecord.employee),
    [employees],
  );

  const submit = React.useCallback(() => {
    void setJobDescription({ id: textRecord.id, description: text }).then(
      () => {
        markAsSeen(textRecord);
        onClose();
      },
    );
  }, [text, textRecord]);

  return (
    <>
      <DefaultModalDialog
        dialogHeaderText="dashboard.newtext.modalHeader"
        submitText="dashboard.newtext.accept"
        isOpen={isOpen}
        showCancelButton
        onCancel={onClose}
        onSubmit={submit}
        initialValues={{}}
        size="4xl"
        alignFooterLeft={true}
        customSubmitButton={
          <SimpleGrid gap={2} columns={2} mr={2}>
            <Button type="submit">dashboard.newtext.accept</Button>
            <Button
              backgroundColor="red.600"
              onClick={() => {
                markAsSeen(textRecord);
                onClose();
              }}
            >
              dashboard.newtext.delete
            </Button>
          </SimpleGrid>
        }
      >
        <Grid
          templateColumns={
            pictures.length > 0 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)'
          }
          gap={3}
        >
          <GridItem colSpan={2} maxH="60vh" overflowY="auto">
            <LabeledText label="dashboard.newtext.project">
              {project?.name}
            </LabeledText>
            <LabeledText label="dashboard.newtext.job">
              {textRecord.job?.name}
            </LabeledText>
            <LabeledText label="dashboard.newtext.employee">
              {formatName(employee)}
            </LabeledText>
            <LabeledText label="dashboard.newtext.department">
              {department?.name}
            </LabeledText>
            <LabeledText label="dashboard.newtext.date">
              {new Date(textRecord.start).toLocaleDateString('de-DE')}
            </LabeledText>
            <FormControl>
              <FormLabel htmlFor={`new-text-${textRecord.id}`}>
                {translate('dashboard.newtext.text')}
              </FormLabel>
              <Textarea
                id={`new-text-${textRecord.id}`}
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={15}
                minW="8vh"
                width={'100%'}
              />
            </FormControl>
          </GridItem>
          <GridItem overflowY="auto" maxH="50vh">
            {pictures?.map((pic) => (
              <Image key={pic.id as string} src={pic.dataURL} />
            ))}
          </GridItem>
        </Grid>
      </DefaultModalDialog>
      {/* Actual Box */}
      <Paper w="full" h="full" pl={3} py={2}>
        <Grid templateColumns="repeat(2,2fr)" columnGap={3}>
          <GridItem>
            <LabeledText label="dashboard.newtext.project">
              {project?.name}
            </LabeledText>
          </GridItem>
          <GridItem></GridItem>
          <GridItem></GridItem>
          <GridItem
            top={0}
            right={0}
            display="flex"
            justifyContent="end"
            gap={3}
          >
            <Tooltip label={translate('dashboard.newtext.navigate.to.project')}>
              <Box cursor="pointer" onClick={() => navigate(`/project/jobs/${textRecord.job?.project}`)}>
                <ExternalLinkIcon color="blue.600" />
              </Box>
            </Tooltip>
            <Tooltip label={translate('dashboard.newtext.view')}>
              <Box cursor="pointer" onClick={onOpen}>
                <ViewIcon color="green.600" />
              </Box>
            </Tooltip>
            <Tooltip label={translate('dashboard.newtext.delete')}>
              <Box cursor="pointer" onClick={() => markAsSeen(textRecord)}>
                <DeleteIcon color="red.600" />
              </Box>
            </Tooltip>
          </GridItem>
          <GridItem>
            <LabeledText label="dashboard.newtext.job">
              {textRecord.job?.name}
            </LabeledText>
          </GridItem>
          <GridItem></GridItem>
          <GridItem></GridItem>
          <GridItem></GridItem>
          <GridItem>
            <LabeledText label="dashboard.newtext.employee">
              {formatName(employee)}
            </LabeledText>
          </GridItem>
          <GridItem colSpan={4}>
            <LabeledText label="dashboard.newtext.department">
              {department?.name}
            </LabeledText>
          </GridItem>
          <GridItem colSpan={4}>
            <LabeledText label="dashboard.newtext.date">
              {new Date(textRecord.start).toLocaleDateString('de-DE')}
            </LabeledText>
          </GridItem>
          <GridItem colSpan={4}>
            <LabeledText label="dashboard.newtext.description" col>
              {textRecord.job?.description}
            </LabeledText>
            {textRecord.comment && (
              <LabeledText label="dashboard.newtext.comment" col>
                {textRecord.comment}
              </LabeledText>
            )}
          </GridItem>
          <GridItem colSpan={4}>
            {textRecord.improvement_comment && (
              <LabeledText label="dashboard.newtext.improvement.comment" col>
                {textRecord.improvement_comment}
              </LabeledText>
            )}
          </GridItem>
        </Grid>
      </Paper>
    </>
  );
};
